import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderSlot as _renderSlot, resolveComponent as _resolveComponent, createVNode as _createVNode, isRef as _isRef, vModelText as _vModelText, createElementVNode as _createElementVNode, withDirectives as _withDirectives, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, normalizeClass as _normalizeClass, createBlock as _createBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

_pushScopeId("data-v-25bd70a4")
const _hoisted_1 = { class: "search-header" }
const _hoisted_2 = { class: "search-input-wrapper" }
const _hoisted_3 = ["placeholder"]
const _hoisted_4 = { class: "search-shortcuts" }
const _hoisted_5 = { class: "shortcut-hint" }
const _hoisted_6 = {
  key: 0,
  class: "search-results"
}
const _hoisted_7 = { class: "section-title" }
const _hoisted_8 = { class: "section-items" }
const _hoisted_9 = ["data-type", "onClick", "onMouseenter"]
const _hoisted_10 = { class: "item-icon" }
const _hoisted_11 = { class: "item-content" }
const _hoisted_12 = { class: "item-title" }
const _hoisted_13 = {
  key: 0,
  class: "item-subtitle"
}
const _hoisted_14 = { class: "item-type" }
const _hoisted_15 = {
  key: 1,
  class: "empty-state"
}
const _hoisted_16 = { class: "empty-title" }
const _hoisted_17 = { class: "empty-subtitle" }
const _hoisted_18 = {
  key: 2,
  class: "help-state"
}
const _hoisted_19 = { class: "help-section" }
const _hoisted_20 = { class: "help-title" }
const _hoisted_21 = { class: "help-shortcuts" }
const _hoisted_22 = { class: "help-shortcut" }
const _hoisted_23 = /*#__PURE__*/_createElementVNode("kbd", null, "↑", -1)
const _hoisted_24 = /*#__PURE__*/_createTextVNode()
const _hoisted_25 = /*#__PURE__*/_createElementVNode("kbd", null, "↓", -1)
const _hoisted_26 = { class: "help-shortcut" }
const _hoisted_27 = /*#__PURE__*/_createElementVNode("kbd", null, "↵", -1)
const _hoisted_28 = { class: "help-shortcut" }
const _hoisted_29 = /*#__PURE__*/_createElementVNode("kbd", null, "esc", -1)
const _hoisted_30 = /*#__PURE__*/_createElementVNode("div", { class: "search-footer" }, [
  /*#__PURE__*/_createElementVNode("div", { class: "footer-shortcuts" }, [
    /*#__PURE__*/_createElementVNode("div", { class: "footer-shortcut" }, [
      /*#__PURE__*/_createElementVNode("kbd", null, "↑"),
      /*#__PURE__*/_createTextVNode(),
      /*#__PURE__*/_createElementVNode("kbd", null, "↓"),
      /*#__PURE__*/_createTextVNode(" to navigate ")
    ]),
    /*#__PURE__*/_createElementVNode("div", { class: "footer-shortcut" }, [
      /*#__PURE__*/_createElementVNode("kbd", null, "↵"),
      /*#__PURE__*/_createTextVNode(" to select")
    ]),
    /*#__PURE__*/_createElementVNode("div", { class: "footer-shortcut" }, [
      /*#__PURE__*/_createElementVNode("kbd", null, "esc"),
      /*#__PURE__*/_createTextVNode(" to close")
    ])
  ])
], -1)
_popScopeId()

import { computed, watch, ref, nextTick } from "vue";
import { useGlobalSearch } from "@/composable/useGlobalSearch";
import { useI18n } from "vue-i18n";


export default /*#__PURE__*/_defineComponent({
  setup(__props) {

const { t } = useI18n();

const {
  isOpen,
  searchQuery,
  selectedIndex,
  searchResults,
  allItems,
  selectItem,
  handleKeydown,
  close
} = useGlobalSearch();

// Template refs
const containerRef = ref<HTMLElement>();
const searchInputRef = ref<HTMLInputElement>();

// Detect if running on Mac
const isMac = computed(() => {
  return (
    typeof navigator !== "undefined" &&
    navigator.platform.toUpperCase().indexOf("MAC") >= 0
  );
});

// Local reactive model for the modal
const localIsOpen = computed({
  get: () => isOpen.value,
  set: (value: boolean) => {
    if (!value) {
      close();
    }
  }
});

// Computed properties with fallbacks for translations
const searchPlaceholder = computed(() => {
  try {
    return t("globalSearch.placeholder");
  } catch {
    return "Search pages and entities...";
  }
});

const noResultsText = computed(() => {
  try {
    return t("globalSearch.noResults");
  } catch {
    return "No results found";
  }
});

const noResultsSubtext = computed(() => {
  try {
    return t("globalSearch.noResultsSubtitle");
  } catch {
    return "Try searching for a different term or entity ID";
  }
});

const helpTitle = computed(() => {
  try {
    return t("globalSearch.helpTitle");
  } catch {
    return "Quick Navigation";
  }
});

const navigateText = computed(() => {
  try {
    return t("globalSearch.navigate");
  } catch {
    return "to navigate";
  }
});

const selectText = computed(() => {
  try {
    return t("globalSearch.select");
  } catch {
    return "to select";
  }
});

const closeText = computed(() => {
  try {
    return t("globalSearch.close");
  } catch {
    return "to close";
  }
});

const hasSearchResults = computed(() => {
  return searchResults.value && searchResults.value.length > 0;
});

// Get global index for an item across all sections
const getGlobalIndex = (sectionIndex: number, itemIndex: number): number => {
  let globalIndex = 0;
  for (let i = 0; i < sectionIndex; i++) {
    globalIndex += searchResults.value[i].items.length;
  }
  return globalIndex + itemIndex;
};

// Get type color for chips
const getTypeColor = (type: string): string => {
  const colors: Record<string, string> = {
    navigation: "primary",
    entity: "success",
    action: "warning",
    "guess-loading": "info",
    "guess-found": "success",
    "guess-error": "danger",
    "guess-action": "info"
  };
  return colors[type] || "secondary";
};

// Get type label
const getTypeLabel = (type: string): string => {
  const labels: Record<string, string> = {
    navigation: "Page",
    entity: "Entity",
    action: "Action",
    "guess-loading": "Finding",
    "guess-found": "Found",
    "guess-error": "Not Found",
    "guess-action": "Find"
  };
  return labels[type] || type;
};

// Auto focus on search input when modal opens
watch(isOpen, newValue => {
  if (newValue) {
    // Use multiple strategies to ensure focus works
    nextTick(() => {
      if (searchInputRef.value) {
        searchInputRef.value.focus();
        searchInputRef.value.select(); // Select all text if any
      }
    });

    // Backup with setTimeout for modal animation
    setTimeout(() => {
      if (
        searchInputRef.value &&
        document.activeElement !== searchInputRef.value
      ) {
        searchInputRef.value.focus();
        searchInputRef.value.select();
      }
    }, 150);
  }
});

// Watch selectedIndex to scroll the selected item into view
watch(selectedIndex, () => {
  nextTick(() => {
    const selectedElement = document.querySelector(".search-item--selected");
    if (selectedElement) {
      selectedElement.scrollIntoView({
        behavior: "smooth",
        block: "nearest"
      });
    }
  });
});

// Reset selected index when search results change
watch(
  searchResults,
  () => {
    selectedIndex.value = 0;
  },
  { deep: true }
);

return (_ctx: any,_cache: any) => {
  const _component_va_icon = _resolveComponent("va-icon")!
  const _component_va_chip = _resolveComponent("va-chip")!
  const _component_va_modal = _resolveComponent("va-modal")!

  return (_openBlock(), _createBlock(_component_va_modal, {
    modelValue: _unref(localIsOpen),
    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => (_isRef(localIsOpen) ? localIsOpen.value = $event : null)),
    size: "large",
    "blur-background": true,
    "hide-default-actions": true,
    "max-width": "800px",
    onKeydown: _unref(handleKeydown),
    class: "global-search-modal"
  }, {
    default: _withCtx(() => [
      _renderSlot(_ctx.$slots, "default", {}, () => [
        _createElementVNode("div", {
          class: "global-search-container",
          tabindex: "-1",
          onKeydown: _cache[1] || (_cache[1] = 
//@ts-ignore
(...args) => (_unref(handleKeydown) && _unref(handleKeydown)(...args))),
          ref: (_value, _refs) => {
            _refs['containerRef'] = _value
            containerRef.value = _value
          }
        }, [
          _createElementVNode("div", _hoisted_1, [
            _createElementVNode("div", _hoisted_2, [
              _createVNode(_component_va_icon, {
                name: "fa4-search",
                class: "search-icon"
              }),
              _withDirectives(_createElementVNode("input", {
                id: "global-search-input",
                ref: (_value, _refs) => {
                  _refs['searchInputRef'] = _value
                  searchInputRef.value = _value
                },
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (_isRef(searchQuery) ? searchQuery.value = $event : null)),
                placeholder: _unref(searchPlaceholder),
                class: "search-input",
                autocomplete: "off",
                spellcheck: "false",
                autofocus: ""
              }, null, 8, _hoisted_3), [
                [_vModelText, _unref(searchQuery)]
              ]),
              _createElementVNode("div", _hoisted_4, [
                _createElementVNode("span", _hoisted_5, _toDisplayString(_unref(isMac) ? "⌘K" : "Ctrl+K"), 1)
              ])
            ])
          ]),
          (_unref(hasSearchResults))
            ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(searchResults), (section, sectionIndex) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: section.title,
                    class: "search-section"
                  }, [
                    _createElementVNode("div", _hoisted_7, _toDisplayString(section.title), 1),
                    _createElementVNode("div", _hoisted_8, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(section.items, (item, itemIndex) => {
                        return (_openBlock(), _createElementBlock("div", {
                          key: item.id,
                          class: _normalizeClass([
                  'search-item',
                  {
                    'search-item--selected':
                      getGlobalIndex(sectionIndex, itemIndex) === _unref(selectedIndex)
                  }
                ]),
                          "data-type": item.type,
                          onClick: ($event: any) => (_unref(selectItem)(item)),
                          onMouseenter: ($event: any) => (
                  selectedIndex.value = getGlobalIndex(sectionIndex, itemIndex)
                )
                        }, [
                          _createElementVNode("div", _hoisted_10, [
                            _createVNode(_component_va_icon, {
                              name: item.icon
                            }, null, 8, ["name"])
                          ]),
                          _createElementVNode("div", _hoisted_11, [
                            _createElementVNode("div", _hoisted_12, _toDisplayString(item.title), 1),
                            (item.subtitle)
                              ? (_openBlock(), _createElementBlock("div", _hoisted_13, _toDisplayString(item.subtitle), 1))
                              : _createCommentVNode("", true)
                          ]),
                          _createElementVNode("div", _hoisted_14, [
                            _createVNode(_component_va_chip, {
                              color: getTypeColor(item.type),
                              size: "small",
                              outline: ""
                            }, {
                              default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(getTypeLabel(item.type)), 1)
                              ]),
                              _: 2
                            }, 1032, ["color"])
                          ])
                        ], 42, _hoisted_9))
                      }), 128))
                    ])
                  ]))
                }), 128))
              ]))
            : (_unref(searchQuery).trim())
              ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                  _createVNode(_component_va_icon, {
                    name: "fa4-search",
                    size: "large",
                    class: "empty-icon"
                  }),
                  _createElementVNode("div", _hoisted_16, _toDisplayString(_unref(noResultsText)), 1),
                  _createElementVNode("div", _hoisted_17, _toDisplayString(_unref(noResultsSubtext)), 1)
                ]))
              : (_openBlock(), _createElementBlock("div", _hoisted_18, [
                  _createElementVNode("div", _hoisted_19, [
                    _createElementVNode("div", _hoisted_20, _toDisplayString(_unref(helpTitle)), 1),
                    _createElementVNode("div", _hoisted_21, [
                      _createElementVNode("div", _hoisted_22, [
                        _hoisted_23,
                        _hoisted_24,
                        _hoisted_25,
                        _createTextVNode(" " + _toDisplayString(_unref(navigateText)), 1)
                      ]),
                      _createElementVNode("div", _hoisted_26, [
                        _hoisted_27,
                        _createTextVNode(" " + _toDisplayString(_unref(selectText)), 1)
                      ]),
                      _createElementVNode("div", _hoisted_28, [
                        _hoisted_29,
                        _createTextVNode(" " + _toDisplayString(_unref(closeText)), 1)
                      ])
                    ])
                  ])
                ])),
          _hoisted_30
        ], 544)
      ])
    ]),
    _: 3
  }, 8, ["modelValue", "onKeydown"]))
}
}

})